// Related merge requests for the focal issue. Sourced from GitLab's
// `related_merge_requests` endpoint — MRs that mention the issue via
// "Closes #iid" or similar. External link to the GitLab MR page.

import type { JSX } from "react";

import type { LinkedMr, MrState } from "@loom/shared/dashboard";

import { useT, type MessageKey } from "../../i18n/index.js";
import { useRelativeTime } from "../../i18n/format.js";

import { Icon } from "./Icon";

const STATE_LABEL_KEY: Record<MrState, MessageKey> = {
  opened: "dashboard.mrs.state.opened",
  merged: "dashboard.mrs.state.merged",
  closed: "dashboard.mrs.state.closed",
  locked: "dashboard.mrs.state.locked",
};

const STATE_VAR: Record<MrState, string> = {
  opened: "var(--st-open)",
  merged: "var(--gate-pass)",
  closed: "var(--st-done)",
  locked: "var(--ink-subtle)",
};

export interface LinkedMrsPanelProps {
  readonly mergeRequests: ReadonlyArray<LinkedMr>;
}

export const LinkedMrsPanel = ({
  mergeRequests,
}: LinkedMrsPanelProps): JSX.Element => {
  const t = useT();
  const rel = useRelativeTime();
  const formatMergedAt = (iso: string | null): string | null => {
    if (!iso) return null;
    const ms = Date.now() - Date.parse(iso);
    if (!Number.isFinite(ms)) return null;
    const d = Math.floor(ms / (1000 * 60 * 60 * 24));
    if (d < 1) return t("dashboard.mrs.mergedToday");
    return t("dashboard.mrs.mergedAgo", { ago: rel(iso) });
  };
  if (mergeRequests.length === 0) {
    return (
      <section className="linked-mrs-panel">
        <h3 className="issue-panel-h">{t("dashboard.mrs.title")}</h3>
        <p className="issue-panel-empty">
          {t("dashboard.mrs.empty")}
        </p>
      </section>
    );
  }
  return (
    <section className="linked-mrs-panel">
      <h3 className="issue-panel-h">{t("dashboard.mrs.title")}</h3>
      {mergeRequests.map((mr) => {
        const merged = formatMergedAt(mr.mergedAt);
        return (
          <a
            key={mr.iid}
            className={`linked-mr-row state-${mr.state}`}
            href={mr.webUrl}
            target="_blank"
            rel="noreferrer"
          >
            <span
              className="linked-mr-state-pip"
              style={{ background: STATE_VAR[mr.state] }}
              title={t(STATE_LABEL_KEY[mr.state])}
            />
            <span className="linked-mr-key">!{mr.iid}</span>
            <span className="linked-mr-title">{mr.title}</span>
            <span className="linked-mr-branches">
              {mr.sourceBranch}
              <Icon name="chevron-up" size={10} />
              {mr.targetBranch}
            </span>
            <span className="linked-mr-state">
              {t(STATE_LABEL_KEY[mr.state])}
              {merged ? ` · ${merged}` : ""}
            </span>
          </a>
        );
      })}
    </section>
  );
};
